import { Form, Formik } from "formik";
import {
  Button,
  Modal,
  Text,
  Stack,
  ButtonGroup,
  Input,
  Shelf,
} from "@chaser/components";
import LabeledFieldWithError from "../../../../../components/LabeledFieldWithError";
import usePush from "../../../../../hooks/usePush";
import useShortCode from "../../../../../hooks/useShortCode";
import SettingsService from "../../../../../services/settings-service";
import useOrganisation from "../../../../../hooks/useOrganisation";
import { IntegrationDisplayNames, roles } from "../../../../../util/constants";
import useUserPermissions from "../../../../../hooks/useUserPermissions";
import { hasRole } from "../../../../../components/FeatureGate/RoleGate";
import useModuleInfo from "../../../../../hooks/useModuleInfo";
import RoleMultiSelect from "../RoleMultiSelect";

function validate(values) {
  const errors = {};

  if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,20}$/i.test(values.email)) {
    errors.email = "Invalid email address";
  }

  return errors;
}

const InviteUserModal = ({
  open,
  close,
  onClosed,
  refetch,
  applyOptimisticUpdate,
  onInviteStart,
  onInviteFinish,
}) => {
  const userPermissions = useUserPermissions();
  const org = useOrganisation();
  const { accountingSoftware } = org;
  const accountingSoftwareName = IntegrationDisplayNames[accountingSoftware];
  const ShortCode = useShortCode();
  const { execute, error: submitError } = usePush(SettingsService.inviteUser, {
    errorToast: true,
  });
  const { refetchSubscriptionData } = useModuleInfo();

  const isAllowedToChangeRoles = hasRole(
    userPermissions,
    [roles.owner, roles.manager, roles.orgAdmin],
    ShortCode,
  );
  const disabledRoles = hasRole(userPermissions, roles.owner, ShortCode)
    ? []
    : [roles.owner];

  return (
    <Formik
      initialValues={{
        email: "",
        selectedRoles: [roles.receivablesViewer],
        open,
      }}
      validate={validate}
      initialErrors={{
        email: "This field is required",
      }}
      onSubmit={async ({ email, selectedRoles }, actions) => {
        onInviteStart?.();

        const permissions = [...selectedRoles];

        const optimisticUser = {
          email,
          roles: permissions,
          verified: false,
          permissions: undefined,
          isLoading: true,
        };

        const apiAction = execute(ShortCode, email, permissions);

        applyOptimisticUpdate((state) => {
          if (!state.invitedChaserUsers) {
            state.invitedChaserUsers = [];
          }
          state.invitedChaserUsers.push(optimisticUser);
        }, apiAction);

        actions.setSubmitting(false);
        close();

        try {
          const [err] = await apiAction;
          if (!err) {
            await Promise.all([
              Promise.resolve(refetch()),
              Promise.resolve(refetchSubscriptionData()),
            ]);
          }
        } finally {
          onInviteFinish?.();
        }
      }}
      enableReinitialize
    >
      {({ isValid, values, setFieldValue }) => (
        <Modal
          open={open}
          toggle={close}
          onClosed={onClosed}
          title="Invite new user"
          body={
            <Form>
              <Stack gap="large">
                <Stack gap="medium">
                  <Text>
                    Enter an email address to invite someone to join{" "}
                    <strong>{org.name || org.Name} on Chaser</strong>.
                  </Text>
                  <Text>
                    The user will not need access to your organisation's{" "}
                    <span>{accountingSoftwareName}</span> account in order to
                    view or chase your invoices in Chaser.
                  </Text>
                  {submitError && <Text color="Red">{submitError}</Text>}
                </Stack>
                <Stack gap="medium">
                  <LabeledFieldWithError
                    data-testid="invite-user-input"
                    data-trackingid="invite-user-input"
                    label="Email address"
                    type="email"
                    name="email"
                    placeholder="Email address for new user"
                    as={Input}
                  />
                  <Text weight="bold">Roles</Text>
                  <RoleMultiSelect
                    roles={values.selectedRoles}
                    disabled={!isAllowedToChangeRoles}
                    disabledRoles={disabledRoles}
                    onChange={(newRoles) =>
                      setFieldValue("selectedRoles", newRoles)
                    }
                  />
                </Stack>
              </Stack>
            </Form>
          }
          footer={
            <Form>
              <ButtonGroup>
                <Button appearance="secondary" onClick={() => close(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="invite-modal-save"
                  data-trackingid="invite-modal-save"
                  disabled={!isValid}
                >
                  Save
                </Button>
              </ButtonGroup>
            </Form>
          }
        />
      )}
    </Formik>
  );
};

export default InviteUserModal;
